import React, { useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useAppDispatch } from "@redux/store";
import { setCartItems } from "@redux/cart/slice";
import { openModal } from "react-url-modal";
import {
  getCartItemsFromStorage,
  setCartItemsToStorage,
} from "@/utils";

import arrowLeftIcon from "@assets/icons/arrowCalendarLeft.svg";
import arrowRightIcon from "@assets/icons/arrowCalendarRight.svg";

import styles from "./Storage.module.scss";

interface StoragePropsType {
  title: string;
}

const MONTH_NAMES = [
  "Январь", "Февраль", "Март", "Апрель", "Май", "Июнь",
  "Июль", "Август", "Сентябрь", "Октябрь", "Ноябрь", "Декабрь",
];

const MAX_STORAGE_SLOTS = 30;
const STORAGE_PRICE = 1000;

export const Storage: React.FC<StoragePropsType> = ({ title }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { auth } = useSelector((state: any) => state.authAndReg);

  const now = new Date();
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [selectedMonths, setSelectedMonths] = useState<Record<string, { month: number; year: number; monthName: string; count: number }>>({});
  const [activeMonthId, setActiveMonthId] = useState<string | null>(null);

  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth();

  const monthsData = useMemo(() => {
    return MONTH_NAMES.map((name, index) => {
      const monthId = `${index}`;
      const isPast = selectedYear < currentYear || (selectedYear === currentYear && index < currentMonth);
      const selected = selectedMonths[monthId];
      return {
        monthId,
        month: index,
        year: selectedYear,
        monthName: name,
        price: STORAGE_PRICE,
        available: isPast ? 0 : MAX_STORAGE_SLOTS,
        selected: !!selected,
        count: selected?.count || 1,
      };
    });
  }, [selectedYear, selectedMonths, currentYear, currentMonth]);

  const handleMonthSelect = (monthId: string) => {
    const item = monthsData.find((m) => m.monthId === monthId);
    if (!item || item.available === 0) return;

    const isSelected = !!selectedMonths[monthId];
    const isActive = activeMonthId === monthId;

    if (isSelected && isActive) {
      const updated = { ...selectedMonths };
      delete updated[monthId];
      setSelectedMonths(updated);
      setActiveMonthId(null);
    } else if (isSelected && !isActive) {
      setActiveMonthId(monthId);
    } else {
      setSelectedMonths({
        ...selectedMonths,
        [monthId]: { month: item.month, year: item.year, monthName: item.monthName, count: 1 },
      });
      setActiveMonthId(monthId);
    }
  };

  const handleCountChange = (delta: number) => {
    if (!activeMonthId || !selectedMonths[activeMonthId]) return;
    const current = selectedMonths[activeMonthId].count;
    const newCount = Math.max(1, Math.min(MAX_STORAGE_SLOTS, current + delta));
    setSelectedMonths({
      ...selectedMonths,
      [activeMonthId]: { ...selectedMonths[activeMonthId], count: newCount },
    });
  };

  const handleAddToCart = useCallback(() => {
    if (!auth) {
      openModal({ name: "login", params: { showWindow: "auth" } });
      return;
    }

    const currentStorage = getCartItemsFromStorage();
    const existingStorage = currentStorage?.storage || {};

    const newStorage = { ...existingStorage };
    Object.values(selectedMonths).forEach((item) => {
      const lastDay = new Date(item.year, item.month + 1, 0);
      const key = `storage-${item.year}-${item.month}`;
      newStorage[key] = {
        month: item.month,
        year: item.year,
        monthName: item.monthName,
        price: STORAGE_PRICE,
        count: item.count,
        startDate: `${item.year}-${String(item.month + 1).padStart(2, "0")}-01T00:00:00`,
        endDate: `${item.year}-${String(item.month + 1).padStart(2, "0")}-${String(lastDay.getDate()).padStart(2, "0")}T23:59:59`,
        storageItem: true,
      };
    });

    const cartItems = {
      ...currentStorage,
      seats: currentStorage?.seats || {},
      rackets: currentStorage?.rackets || {},
      products: currentStorage?.products || {},
      storage: newStorage,
    };

    setCartItemsToStorage(cartItems);
    dispatch(setCartItems(cartItems));
    setSelectedMonths({});
    setActiveMonthId(null);
    navigate("/cart");
  }, [auth, selectedMonths, dispatch, navigate]);

  const hasSelected = Object.keys(selectedMonths).length > 0;
  const activeMonth = activeMonthId ? selectedMonths[activeMonthId] : null;

  return (
    <div className={styles.container}>
      <div className={styles.yearSelector}>
        <button
          className={styles.yearArrow}
          onClick={() => setSelectedYear((y) => y - 1)}
          aria-label="Предыдущий год"
        >
          <img src={arrowLeftIcon} alt="" />
        </button>
        <span className={styles.yearText}>{selectedYear} год</span>
        <button
          className={styles.yearArrow}
          onClick={() => setSelectedYear((y) => y + 1)}
          aria-label="Следующий год"
        >
          <img src={arrowRightIcon} alt="" />
        </button>
      </div>

      <h3 className={styles.title}>Выберите месяц и количество ячеек</h3>

      <div className={styles.grid}>
        {monthsData.map((item) => {
          const isSelected = !!selectedMonths[item.monthId];
          const isActive = activeMonthId === item.monthId;
          const isDisabled = item.available === 0;

          return (
            <button
              key={item.monthId}
              className={`${styles.cell} ${isDisabled ? styles.cellDisabled : ""} ${isSelected ? styles.cellSelected : ""} ${isActive ? styles.cellActive : ""}`}
              disabled={isDisabled}
              onClick={() => handleMonthSelect(item.monthId)}
            >
              <span className={`${styles.cellMonth} ${isSelected ? styles.cellTextWhite : ""}`}>
                {item.monthName}
              </span>
              <span className={`${styles.cellPrice} ${isSelected ? styles.cellTextWhite : ""}`}>
                {item.price} р/мес
              </span>
              {isSelected && selectedMonths[item.monthId] && (
                <span className={styles.countBadge}>{selectedMonths[item.monthId].count}</span>
              )}
            </button>
          );
        })}
      </div>

      {hasSelected && activeMonth && (
        <div className={styles.footer}>
          <div className={styles.counter}>
            <button
              className={styles.counterBtn}
              onClick={() => handleCountChange(-1)}
              disabled={activeMonth.count <= 1}
            >
              -
            </button>
            <span className={styles.counterValue}>{activeMonth.count}</span>
            <button
              className={styles.counterBtn}
              onClick={() => handleCountChange(1)}
              disabled={activeMonth.count >= MAX_STORAGE_SLOTS}
            >
              +
            </button>
          </div>
          <button className={styles.addToCartBtn} onClick={handleAddToCart}>
            В корзину
          </button>
        </div>
      )}
    </div>
  );
};
